import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { XIcon, AlertCircleIcon, BookOpenIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
interface IncompleteLesson {
  moduleId: string;
  moduleName: string;
  lessonId: string;
  lessonTitle: string;
}
interface IncompleteLessonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseTitle: string;
  incompleteLessons: IncompleteLesson[];
}
export function IncompleteLessonsModal({
  isOpen,
  onClose,
  courseId,
  courseTitle,
  incompleteLessons
}: IncompleteLessonsModalProps) {
  return <AnimatePresence data-id="element-984">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-985" />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" data-id="element-986">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden relative max-h-[90vh] overflow-y-auto" data-id="element-987">
              {/* Close button */}
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10" data-id="element-988">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-989" />
              </button>

              {/* Content */}
              <div className="relative p-8" data-id="element-990">
                {/* Warning Icon */}
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.2
            }} className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-xl bg-gradient-to-br from-amber-400 to-orange-500" data-id="element-991">
                  <AlertCircleIcon className="w-14 h-14 text-white" data-id="element-992" />
                </motion.div>

                {/* Title */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-center mb-6" data-id="element-993">
                  <h2 className="text-3xl font-bold text-[#132C53] mb-3" data-id="element-994">
                    Training Incomplet
                  </h2>
                  <p className="text-lg text-gray-600" data-id="element-995">
                    Nu ai finalizat toate lecțiile din
                  </p>
                  <p className="text-xl font-bold text-[#357BEB] mt-1" data-id="element-996">
                    {courseTitle}
                  </p>
                </motion.div>

                {/* Info Message */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 mb-6" data-id="element-997">
                  <div className="flex items-start space-x-3" data-id="element-998">
                    <AlertCircleIcon className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" data-id="element-999" />
                    <div data-id="element-1000">
                      <h3 className="font-bold text-amber-900 mb-2" data-id="element-1001">
                        Pentru a finaliza training-ul
                      </h3>
                      <p className="text-sm text-amber-800 leading-relaxed" data-id="element-1002">
                        Trebuie să parcurgi și să finalizezi toate lecțiile din
                        training. Acest lucru asigură că ai învățat toate
                        conceptele necesare pentru a avansa.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Incomplete Lessons List */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="mb-6" data-id="element-1003">
                  <h3 className="font-bold text-[#132C53] mb-4 flex items-center" data-id="element-1004">
                    <BookOpenIcon className="w-5 h-5 mr-2 text-[#357BEB]" data-id="element-1005" />
                    Lecții nefinalizate ({incompleteLessons.length})
                  </h3>

                  <div className="space-y-3 max-h-64 overflow-y-auto" data-id="element-1006">
                    {incompleteLessons.map((lesson, index) => <motion.div key={`${lesson.moduleId}-${lesson.lessonId}`} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.6 + index * 0.05
                }} data-id="element-1007">
                        <Link to={`/curs/${courseId}/modul/${lesson.moduleId}/lectie/${lesson.lessonId}`} onClick={onClose} className="block bg-white border-2 border-gray-200 hover:border-[#357BEB] rounded-xl p-4 transition-all hover:shadow-md group" data-id="element-1008">
                          <div className="flex items-start justify-between" data-id="element-1009">
                            <div className="flex-1" data-id="element-1010">
                              <p className="text-xs font-semibold text-gray-500 mb-1" data-id="element-1011">
                                {lesson.moduleName}
                              </p>
                              <p className="font-semibold text-[#132C53] group-hover:text-[#357BEB] transition-colors" data-id="element-1012">
                                {lesson.lessonTitle}
                              </p>
                            </div>
                            <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-[#357BEB] transition-colors flex-shrink-0 ml-3" data-id="element-1013" />
                          </div>
                        </Link>
                      </motion.div>)}
                  </div>
                </motion.div>

                {/* Progress Info */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-6" data-id="element-1014">
                  <div className="flex items-start space-x-3" data-id="element-1015">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-1016" />
                    <div data-id="element-1017">
                      <h4 className="font-bold text-blue-900 mb-2" data-id="element-1018">
                        Progresul tău
                      </h4>
                      <p className="text-sm text-blue-800" data-id="element-1019">
                        După ce finalizezi toate lecțiile, vei putea să
                        completezi training-ul și să primești punctele de
                        finalizare.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Action Button */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.8
            }} data-id="element-1020">
                  <button onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2" data-id="element-1021">
                    <span data-id="element-1022">Continuă training-ul</span>
                    <ArrowRightIcon className="w-5 h-5" data-id="element-1023" />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}